import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Loader2, Check, Bot, PiggyBank } from 'lucide-react';

const fmt = (n) => `$${n.toFixed(2).replace(/\.00$/, '')}`;
const PROVIDERS = ['openai', 'anthropic', 'gemini'];
const PROVIDER_LABELS = { openai: 'OpenAI', anthropic: 'Anthropic', gemini: 'Google Gemini' };

// Admin card (System page): model selection, daily limit, monthly team budget, AC access toggle.
export const AiSettingsCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState('');
  const [limit, setLimit] = useState('30');
  const [budget, setBudget] = useState('0');
  const [acEnabled, setAcEnabled] = useState(false);

  useEffect(() => {
    api.getAiSettings()
      .then(r => {
        setData(r.data);
        setSel(`${r.data.provider}|${r.data.model}`);
        setLimit(String(r.data.daily_limit_per_user));
        setBudget(String(r.data.monthly_budget_usd ?? 0));
        setAcEnabled(!!r.data.ac_ai_enabled);
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to load AI settings'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    const [provider, model] = sel.split('|');
    const n = parseInt(limit, 10);
    if (isNaN(n) || n < 0 || n > 1000) { toast.error('Limit: 0 (unbegrenzt) bis 1000'); return; }
    const b = parseFloat(budget);
    if (isNaN(b) || b < 0 || b > 100000) { toast.error('Budget: 0 (aus) bis 100000 USD'); return; }
    setSaving(true);
    try {
      const r = await api.saveAiSettings({ provider, model, daily_limit_per_user: n, monthly_budget_usd: b, ac_ai_enabled: acEnabled });
      setData(d => ({ ...d, ...r.data }));
      setSel(`${r.data.provider}|${r.data.model}`);
      setLimit(String(r.data.daily_limit_per_user));
      setBudget(String(r.data.monthly_budget_usd ?? 0));
      setAcEnabled(!!r.data.ac_ai_enabled);
      toast.success('AI-Einstellungen gespeichert');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const current = data?.models?.find(m => `${m.provider}|${m.model}` === sel);
  const cheapest = data?.cheapest;
  const isCheapest = (m) => cheapest && m.provider === cheapest.provider && m.model === cheapest.model;

  const budgetNum = parseFloat(budget) || 0;
  const monthCost = data?.month_cost ?? 0;
  const budgetPct = budgetNum > 0 ? Math.min(100, Math.round((monthCost / budgetNum) * 100)) : 0;
  const budgetColor = budgetPct >= 100 ? 'text-red-400' : budgetPct >= 80 ? 'text-amber-400' : 'text-emerald-400';
  const barColor = budgetPct >= 100 ? 'bg-red-500' : budgetPct >= 80 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="ai-settings-card">
      <div className="flex items-center gap-2 mb-1">
        <Bot className="w-4 h-4 text-violet-400" />
        <p className="font-heading text-base text-foreground">Ask OLTA AI — Modell & Limits</p>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Gilt für Ask OLTA AI auf den Deal-Seiten und im Activity Feed. Das Tageslimit gilt pro Nutzer —
        auch für Admins. Preise: ca. USD pro 1 Mio. Tokens (Input / Output).
      </p>
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      ) : !data ? null : (
        <div className="space-y-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider">KI-Modell</label>
            <select value={sel} onChange={e => setSel(e.target.value)} data-testid="ai-model-select"
              className="max-w-md text-sm bg-secondary border border-border text-foreground rounded-sm px-2 py-1.5 cursor-pointer focus:border-violet-500 focus:outline-none">
              {PROVIDERS.map(p => (
                <optgroup key={p} label={PROVIDER_LABELS[p]}>
                  {data.models.filter(m => m.provider === p).map(m => (
                    <option key={m.model} value={`${m.provider}|${m.model}`}>
                      {m.label} — {fmt(m.input)} / {fmt(m.output)}{isCheapest(m) ? ' · GÜNSTIGSTES MODELL' : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {current && (
              <p className="text-[11px] text-muted-foreground" data-testid="ai-current-price">
                Ausgewählt: <span className="text-foreground font-medium">{current.label}</span> — {fmt(current.input)} Input / {fmt(current.output)} Output pro 1M Tokens
                {isCheapest(current) && <span className="text-emerald-400 ml-1">(günstigstes Modell)</span>}
              </p>
            )}
          </div>

          {cheapest && (
            <p className="text-xs flex items-center gap-1.5 text-emerald-400" data-testid="ai-cheapest-hint">
              <PiggyBank className="w-3.5 h-3.5 shrink-0" />
              <span>Günstigstes Modell: <b>{cheapest.label}</b> ({fmt(cheapest.input)} Input / {fmt(cheapest.output)} Output pro 1M Tokens)</span>
            </p>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">Tageslimit</span>
            <Input type="number" min={0} max={1000} value={limit} onChange={e => setLimit(e.target.value)}
              data-testid="ai-limit-input"
              className="bg-secondary border-border text-foreground rounded-sm h-8 text-sm w-24" />
            <span className="text-xs text-muted-foreground">Anfragen pro Nutzer & Tag (0 = unbegrenzt)</span>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Monatsbudget</span>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                <Input type="number" min={0} max={100000} step="0.5" value={budget} onChange={e => setBudget(e.target.value)}
                  data-testid="ai-budget-input"
                  className="bg-secondary border-border text-foreground rounded-sm h-8 text-sm w-28 pl-5" />
              </div>
              <span className="text-xs text-muted-foreground">USD pro Monat fürs gesamte Team (0 = aus) — bei 80% Push an Admins, bei 100% pausiert die KI</span>
            </div>
            {budgetNum > 0 && (
              <div className="max-w-md" data-testid="ai-budget-progress">
                <div className="flex items-center justify-between text-[11px] font-mono mb-1">
                  <span className={budgetColor}>Diesen Monat: ${monthCost.toFixed(2)} von ${budgetNum.toFixed(2)}</span>
                  <span className={budgetColor}>{budgetPct}%</span>
                </div>
                <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                  <div className={`h-full ${barColor} transition-all`} style={{ width: `${budgetPct}%` }} />
                </div>
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-xs text-muted-foreground select-none" data-testid="ai-ac-toggle">
            <input type="checkbox" checked={acEnabled} onChange={e => setAcEnabled(e.target.checked)}
              data-testid="ai-ac-toggle-checkbox" className="accent-violet-500 w-3.5 h-3.5" />
            AC-Mitglieder dürfen Ask OLTA AI nutzen
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${acEnabled ? 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' : 'text-red-400 border-red-500/30 bg-red-500/10'}`}>
              {acEnabled ? 'AN' : 'AUS'}
            </span>
          </label>

          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={save} disabled={saving} data-testid="ai-settings-save-btn"
              className="bg-violet-700 hover:bg-violet-600 text-white rounded-sm text-xs h-8 px-3">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
              Save
            </Button>
            <span className="text-[11px] text-muted-foreground font-mono" data-testid="ai-today-total">
              Heute gesamt: {data.today_total} Anfrage{data.today_total !== 1 ? 'n' : ''} (Team)
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
